import { Injectable, inject, signal, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

export interface GpsResult {
  lat: number | null;
  lng: number | null;
  location_unavailable: boolean;
  gps_denied: boolean;
  accuracy?: number | null;
}

/** Timeout in milliseconds for GPS fix */
const GPS_TIMEOUT_MS = 10_000;

/** Default interval in seconds for continuous location reporting */
const DEFAULT_REPORTING_INTERVAL_S = 60;

/** Minimum allowed interval in seconds */
const MIN_REPORTING_INTERVAL_S = 10;

/** Maximum allowed interval in seconds */
const MAX_REPORTING_INTERVAL_S = 300;

@Injectable({ providedIn: 'root' })
export class GpsService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  /** Whether GPS permission has been denied by the user */
  readonly gpsDenied = signal(false);

  /** Whether the last GPS attempt resulted in location unavailable */
  readonly locationUnavailable = signal(false);

  /** Whether continuous location reporting is currently active */
  readonly isReporting = signal(false);

  /** Interval timer ID for continuous reporting */
  private reportingIntervalId: ReturnType<typeof setInterval> | null = null;

  /** Resource ID for the current reporting session */
  private reportingResourceId: string | null = null;

  /** Tenant ID for the current reporting session */
  private reportingTenantId: string | null = null;

  ngOnDestroy(): void {
    this.stopContinuousReporting();
  }

  /**
   * Request the device's current GPS position.
   * Resolves with coordinates on success, or null coordinates with
   * appropriate flags on permission denial or timeout/failure.
   */
  getCurrentPosition(): Promise<GpsResult> {
    if (!navigator.geolocation) {
      this.locationUnavailable.set(true);
      return Promise.resolve({
        lat: null,
        lng: null,
        location_unavailable: true,
        gps_denied: false,
        accuracy: null,
      });
    }

    return new Promise<GpsResult>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.gpsDenied.set(false);
          this.locationUnavailable.set(false);
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            location_unavailable: false,
            gps_denied: false,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            this.gpsDenied.set(true);
            this.locationUnavailable.set(false);
            resolve({
              lat: null,
              lng: null,
              location_unavailable: false,
              gps_denied: true,
              accuracy: null,
            });
          } else {
            // TIMEOUT or POSITION_UNAVAILABLE
            this.gpsDenied.set(false);
            this.locationUnavailable.set(true);
            resolve({
              lat: null,
              lng: null,
              location_unavailable: true,
              gps_denied: false,
              accuracy: null,
            });
          }
        },
        {
          enableHighAccuracy: true,
          timeout: GPS_TIMEOUT_MS,
          maximumAge: 0,
        }
      );
    });
  }

  /**
   * Prompt for GPS permission on first use by triggering a geolocation request.
   * Returns the permission state or the result of the initial position request.
   */
  async promptForPermission(): Promise<GpsResult> {
    // Attempt to use the Permissions API to check current state
    if (navigator.permissions) {
      try {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        if (status.state === 'denied') {
          this.gpsDenied.set(true);
          return {
            lat: null,
            lng: null,
            location_unavailable: false,
            gps_denied: true,
            accuracy: null,
          };
        }
      } catch {
        // Permissions API not supported for geolocation; fall through to getCurrentPosition
      }
    }

    // Trigger the browser permission prompt by requesting position
    return this.getCurrentPosition();
  }

  /**
   * Start continuous location reporting at the specified interval.
   * Reports the resource's GPS position to the backend periodically.
   *
   * Called on clock-in. Stops automatically on clock-out or service destroy.
   *
   * @param resourceId - The resource ID to report location for
   * @param tenantId - The tenant ID for the API path
   * @param intervalSeconds - Reporting interval (default 60s, clamped to 10-300s)
   *
   * Validates: Requirements 5.1, 5.5
   */
  startContinuousReporting(
    resourceId: string,
    tenantId: string,
    intervalSeconds?: number
  ): void {
    // Stop any existing reporting session first
    this.stopContinuousReporting();

    this.reportingResourceId = resourceId;
    this.reportingTenantId = tenantId;

    // Clamp interval to allowed range
    const interval = Math.max(
      MIN_REPORTING_INTERVAL_S,
      Math.min(MAX_REPORTING_INTERVAL_S, intervalSeconds ?? DEFAULT_REPORTING_INTERVAL_S)
    );

    this.isReporting.set(true);

    // Perform an immediate first report
    this.reportLocationCycle();

    // Set up the interval for subsequent reports
    this.reportingIntervalId = setInterval(
      () => this.reportLocationCycle(),
      interval * 1000
    );
  }

  /**
   * Stop continuous location reporting.
   * Called on clock-out or service destroy.
   *
   * Validates: Requirements 5.5
   */
  stopContinuousReporting(): void {
    if (this.reportingIntervalId !== null) {
      clearInterval(this.reportingIntervalId);
      this.reportingIntervalId = null;
    }
    this.reportingResourceId = null;
    this.reportingTenantId = null;
    this.isReporting.set(false);
  }

  /**
   * Report the current location immediately, outside the regular interval.
   * Used when a "report_location" push notification is received.
   *
   * If continuous reporting is already active, this performs an immediate
   * extra report without resetting the interval timer.
   *
   * @param resourceId - The resource ID (used when reporting is not active)
   * @param tenantId - The tenant ID (used when reporting is not active)
   *
   * Validates: Requirements 9.5
   */
  async reportLocationNow(resourceId: string, tenantId: string): Promise<void> {
    // Use active session IDs if reporting, otherwise use provided params
    const rid = this.reportingResourceId ?? resourceId;
    const tid = this.reportingTenantId ?? tenantId;

    await this.performLocationReport(rid, tid);
  }

  /**
   * A single reporting cycle: get GPS fix and POST to backend.
   * Skips silently if GPS is unavailable or permission denied.
   *
   * Validates: Requirements 5.6
   */
  private async reportLocationCycle(): Promise<void> {
    const resourceId = this.reportingResourceId;
    const tenantId = this.reportingTenantId;
    if (!resourceId || !tenantId) return;

    await this.performLocationReport(resourceId, tenantId);
  }

  /**
   * Perform a single location report: get GPS fix → POST to backend.
   * Skips silently on GPS timeout or permission denied.
   */
  private async performLocationReport(resourceId: string, tenantId: string): Promise<void> {
    try {
      const gpsResult = await this.getCurrentPosition();

      // Skip this cycle if GPS fix unavailable or permission denied
      if (gpsResult.gps_denied || gpsResult.location_unavailable) {
        console.warn(
          '[GpsService] Skipping location report:',
          gpsResult.gps_denied ? 'permission denied' : 'location unavailable'
        );
        return;
      }

      const body = {
        lat: gpsResult.lat,
        lng: gpsResult.lng,
        timestamp: new Date().toISOString(),
        accuracy: gpsResult.accuracy ?? 0,
      };

      const url = `${this.baseUrl}/tenants/${tenantId}/resources/${resourceId}/location`;
      await firstValueFrom(this.http.post(url, body));
    } catch (error) {
      // Log and skip — do not throw or queue failed updates
      console.warn('[GpsService] Location report failed:', error);
    }
  }
}
